// Joins one Explore world (through a small host) to one RoomClient (docs/MULTIPLAYER.md).
// Local edits become ops, snapshots and friends' edits become list changes, refused ops are rolled back.
import { cellKey, targetFrom, diffLists, deltaToChanges, inverseOf, isMine, seedOps, takenDownOps, hasChanges } from './room-mirror.js';

export const PENDING_CAP = 64; // own world: pending ops travel in the cloud save
const GAINS = new Set(['remove', 'mine', 'harvest']);
const arr = (v) => (Array.isArray(v) ? v : []);
const slot = (op) => `${op.k === 'place' || op.k === 'remove' ? 'block' : op.k}:${cellKey(op)}`;
const holds = (lists, op) => arr(lists?.blocks).some((b) => b[0] === op.x && b[1] === op.y && b[2] === op.z && b[3] === op.id);

// Own world: the save keeps edits until the room has them. Past PENDING_CAP the queue folds into the local world.
// Each removal becomes a tombstone in `takenDown` ([x,y,z,id], or [x,y,z,id,1] for a friend's block), because a
// copy of the local lists can't say that a block is gone. The newest state of each door, switch and drawbridge
// stays queued. Everything else is copied again from the local lists on the next connection (worldSeeded = false).
export function holdOp(d, op) {
  const pending = (d.pending ||= []);
  pending.push(op);
  if (pending.length <= PENDING_CAP) return false;
  const gone = (d.takenDown ||= []), flips = new Map();
  for (const o of pending) {
    const k = cellKey(o);
    if (o.k === 'remove' && Number.isInteger(o.id)) {
      o.tomb = o.prevOwner == null ? [o.x, o.y, o.z, o.id] : [o.x, o.y, o.z, o.id, 1]; // links a sent op to its tombstone
      gone.push(o.tomb);
      flips.delete(k);
    } else if (o.k === 'device') {
      flips.delete(k);
      flips.set(k, o);
    }
  }
  pending.length = 0;
  pending.push(...[...flips.values()].slice(-PENDING_CAP));
  d.worldSeeded = false;
  return true;
}

export class WorldSync {
  constructor({ client, host, mode = 'own', me = null, worldOwner = null, pending = [], discovery = null } = {}) {
    this.client = client;
    this.host = host;
    this.mode = mode;
    this.me = me;
    this.worldOwner = worldOwner;
    this.pending = pending || [];
    this.discovery = discovery || {};
    if (mode === 'own') this.discovery.pending = this.pending;
    this.inflight = new Map(); // s -> {op, seed}
    this.voided = new WeakSet(); // removes of blocks the room never had: nothing to undo
    this.seeding = null;
    this.synced = false; // a snapshot was reconciled on the current connection
    this.epoch = null;
    this.last = null;
    this.offs = [
      client.on('snapshot', (m) => this.handleSnapshot(m)),
      client.on('acks', (m) => this.handleAcks(m)),
      client.on('cells', (m) => this.handleCells(m)),
      client.on('reset', () => (this.synced = false)),
      client.on('status', (s) => s !== 'online' && this.lost()),
    ];
  }

  record(op) {
    if (!op || typeof op.k !== 'string') return false;
    if (this.mode !== 'own') this.pending.push(op);
    else if (holdOp(this.discovery, op)) this.seeding = null; // folded: the next connection copies the world again
    if (this.synced) this.transmit([op]);
    this.persist();
    return true;
  }

  // Also call this with a snapshot that arrived before this WorldSync existed.
  handleSnapshot(snap) {
    if (!snap?.world || snap === this.last) return;
    this.last = snap;
    const own = this.mode === 'own', d = this.discovery, room = snap.room || {};
    if (snap.you?.id) this.me = snap.you.id;
    if (room.ownerId) this.worldOwner = room.ownerId;
    this.inflight.clear();
    this.seeding = null;
    const epoch = room.epoch ?? null, known = own ? (d.worldEpoch ?? null) : this.epoch;
    if (known !== null && epoch !== null && known !== epoch) {
      // The world was started again (or brought back): edits for the old one no longer apply.
      this.pending.length = 0;
      if (own) {
        d.worldSeeded = true;
        d.takenDown = [];
      }
    }
    this.epoch = epoch;
    if (own) d.worldEpoch = epoch;
    const worldTime = Number(this.host.worldTime()) || 0;
    // Tombstones go first: they were taken down before anything still pending.
    const tombs = own ? takenDownOps(d.takenDown) : [];
    const seeds = own && !d.worldSeeded ? this.seeds(snap.world, worldTime, tombs) : [];
    const { target, kept, dropped } = targetFrom(snap.world, [...tombs, ...seeds, ...this.pending], { me: this.me, worldOwner: this.worldOwner, worldTime });
    for (const op of dropped) {
      this.undoItems(op);
      this.host.rejected?.(op, 'conflict');
    }
    const keep = new Set(kept);
    for (const op of tombs) if (!keep.has(op)) this.settle(op); // already gone from the room
    let w = 0;
    for (const op of this.pending) if (keep.has(op)) this.pending[w++] = op;
    this.pending.length = w;
    const changes = diffLists(this.host.lists(), target, { worldTime });
    if (hasChanges(changes)) this.host.applyChanges(changes, 'snapshot');
    this.synced = true;
    const seedSet = new Set(seeds), sent = kept.length ? this.transmit(kept, seedSet) : [];
    if (own && !d.worldSeeded) {
      const n = kept.filter((op) => seedSet.has(op)).length;
      if (!n) d.worldSeeded = true;
      else if (sent.length === kept.length) this.seeding = { left: n };
    }
    this.persist();
  }

  handleAcks(msg) {
    const no = arr(msg?.no).filter((e) => Array.isArray(e) && this.inflight.has(e[0])).sort((a, b) => b[0] - a[0]);
    const ok = arr(msg?.ok).filter((s) => this.inflight.has(s));
    if (!no.length && !ok.length) return;
    // Newest first, so a refused remove-then-place puts the original block back.
    for (const [s, reason] of no) this.refuse(this.take(s), typeof reason === 'string' ? reason : 'bad');
    for (const s of ok) this.unpend(this.take(s));
    this.persist();
  }

  handleCells(msg) {
    const changes = deltaToChanges(msg?.ch);
    if (hasChanges(changes)) this.host.applyChanges(changes, 'remote');
  }

  dispose() {
    for (const off of this.offs.splice(0)) off?.();
    this.lost();
  }

  // ---------- internals ----------
  // The socket is gone: nothing in flight will be answered, and pending is replayed on the next snapshot.
  lost() {
    this.synced = false;
    this.inflight.clear();
    this.seeding = null;
  }

  // A copy of the local world for cells no pending op covers; blocks the room already has need no copy,
  // unless a tombstone takes the room's block away first.
  seeds(world, worldTime, tombs = []) {
    const covered = new Set(this.pending.map(slot));
    const there = new Map(arr(world.cells).filter(Array.isArray).map((c) => [cellKey(c), c[3]]));
    for (const t of tombs) if (there.get(cellKey(t)) === t.id) there.delete(cellKey(t));
    // A fold may evict a device op too: the saved OFF state is as authoritative as ON.
    return seedOps(this.host.lists(), worldTime, { includeOff: true }).filter(
      (op) => !covered.has(slot(op)) && !(op.k === 'place' && there.get(cellKey(op)) === op.id),
    );
  }

  transmit(ops, seeds) {
    const seqs = this.client.sendOps(ops) || [];
    seqs.forEach((s, i) => this.inflight.set(s, { op: ops[i], seed: !!seeds?.has(ops[i]) }));
    return seqs;
  }

  take(s) {
    const e = this.inflight.get(s);
    this.inflight.delete(s);
    if (e.seed && this.seeding && --this.seeding.left <= 0) {
      this.seeding = null;
      this.discovery.worldSeeded = true;
    }
    this.settle(e.op);
    return e.op;
  }

  // The room has answered a removal that was kept as a tombstone, so the tombstone can go.
  settle(op) {
    const list = this.discovery.takenDown, i = op.tomb && Array.isArray(list) ? list.indexOf(op.tomb) : -1;
    if (i >= 0) list.splice(i, 1);
  }

  unpend(op) {
    const i = this.pending.indexOf(op);
    if (i >= 0) this.pending.splice(i, 1);
  }

  refuse(op, reason) {
    if (this.voided.has(op)) return this.unpend(op);
    const i = Math.max(0, this.pending.indexOf(op));
    this.unpend(op);
    const lists = this.host.lists();
    if (op.k === 'place' && !(holds(lists, op) && isMine(lists, op, this.me, this.worldOwner))) {
      // Our block is not there any more. If we took it down ourselves, that remove already returned it.
      const later = this.pending.find((o, j) => j >= i && o.k === 'remove' && cellKey(o) === cellKey(op) && o.item === op.item && (o.n ?? 1) > 0);
      if (later) {
        this.voided.add(later);
        return this.unpend(later);
      }
      // ...or that remove was folded into a tombstone.
      const gone = arr(this.discovery.takenDown), tomb = gone.find((t) => cellKey(t) === cellKey(op) && t[3] === op.id && t[4] !== 1);
      if (tomb) {
        gone.splice(gone.indexOf(tomb), 1);
        return;
      }
    } else this.host.applyChanges(inverseOf(op), 'rollback');
    this.undoItems(op);
    this.host.rejected?.(op, reason);
  }

  undoItems(op) {
    const n = op.n ?? 1;
    if (!op.item || !Number.isSafeInteger(n) || n <= 0) return;
    if (op.k === 'place') this.host.adjustItem(op.item, n);
    else if (GAINS.has(op.k)) this.host.adjustItem(op.item, -n);
  }

  persist() {
    if (this.mode === 'own') this.host.persist?.();
  }
}
